/**
 * Detects fake / placeholder Indian mobile numbers that visitors type to
 * skip our lead form ("9876543210", "9999999999" etc). These wastes our
 * SMS budget and pollute CRM funnel analytics, so we filter them BEFORE
 * firing any /service-leads or /draft API call.
 *
 * Returns `null` if the number looks real, or a human-readable reason
 * string if it looks fake — surface this directly under the input field.
 *
 * Rules (in order):
 *   1. Must be exactly 10 digits
 *   2. Must start with 6-9 (TRAI assignment range)
 *   3. NOT a known blocklisted number (operator test, Bollywood, etc.)
 *   4. NOT a single digit repeated 7+ times (9999999999, 7777788888)
 *   5. NOT a 6+ length sequential run, ascending or descending
 *      (9876543210, 1234567890, 9012345678 — kept as a phone-keypad sequence
 *      is the most common "fake" people type)
 *
 * Real Indian mobiles can technically have legit repeats (e.g. 9988776655
 * which has no 6-run) so we tune for HIGH recall of fakes with LOW false-
 * positives on real numbers.
 *
 * @param {string} mobile  Raw input — may contain spaces/+91/etc.
 * @returns {string|null}  null = valid; otherwise the error message.
 */
export function detectFakeMobile(mobile) {
  const digits = String(mobile || "").replace(/\D/g, "").slice(-10);

  if (digits.length !== 10) {
    return "Enter a valid 10-digit Indian mobile number";
  }
  if (!/^[6-9]/.test(digits)) {
    return "Indian mobile numbers start with 6, 7, 8 or 9";
  }

  // --- Rule 3: explicit blocklist ------------------------------------
  // The numbers people type most often when they don't want to share a
  // real one. Kept short — pattern rules below catch the long tail.
  const KNOWN_FAKES = new Set([
    "9876543210",
    "1234567890",
    "0123456789",
    "9999999999",
    "8888888888",
    "7777777777",
    "6666666666",
    "9090909090",
    "8080808080",
    "7070707070",
    "9000000000",
    "8000000000",
    "7000000000",
    "9123456789",
    "9988776655",
    "9876512345",
    "9999988888",
    "7894561230", // "phone keypad rotated"
    "1472583690", // "phone keypad rotated"
  ]);
  if (KNOWN_FAKES.has(digits)) {
    return "This looks like a test number — please use your real mobile";
  }

  // --- Rule 4: dominant repeated digit -------------------------------
  // 7+ of the 10 digits are identical. Catches 9999999999, 9999988888,
  // 8888899999, etc. Real numbers rarely have this skew.
  const counts = {};
  for (const c of digits) counts[c] = (counts[c] || 0) + 1;
  const maxCount = Math.max(...Object.values(counts));
  if (maxCount >= 7) {
    return "This looks like a test number — please use your real mobile";
  }

  // --- Rule 5: long sequential run -----------------------------------
  // Scans for a 6+ length monotonic step-of-1 sequence (asc or desc).
  // 9876543210 has a 10-run desc → fake. A real number like 9845671234
  // has a max 4-run (1234) → passes.
  let runLen = 1, runDir = 0;
  let maxRun = 1;
  for (let i = 1; i < digits.length; i++) {
    const step = Number(digits[i]) - Number(digits[i - 1]);
    if (step === 1 || step === -1) {
      if (step === runDir) {
        runLen++;
      } else {
        runLen = 2;
        runDir = step;
      }
      if (runLen > maxRun) maxRun = runLen;
    } else {
      runLen = 1;
      runDir = 0;
    }
  }
  if (maxRun >= 6) {
    return "This looks like a test number — please use your real mobile";
  }

  return null;
}

/**
 * Convenience boolean wrapper. Returns `true` only when the mobile passes
 * every fake-number heuristic — safe to use as a gate before any API call.
 */
export function isLikelyRealIndianMobile(mobile) {
  return detectFakeMobile(mobile) === null;
}
